<template>
  <div class="reset-panel">
    <div class="reset-card">
      <!-- <div class="terminal-label">POS TERMINAL</div>
      <h1 class="greeting">Reset PIN!</h1>
      <p class="subtitle">
        Your password PIN has expired. Please set a new PIN to continue.
      </p> -->

      <!-- Alert Messages -->
      <div v-if="errorMessage" class="alert-bar alert-error" role="alert">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle
            cx="8"
            cy="8"
            r="7"
            stroke="currentColor"
            stroke-width="1.5"
          />
          <line
            x1="8"
            y1="5"
            x2="8"
            y2="8.5"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          />
          <circle cx="8" cy="11" r="0.75" fill="currentColor" />
        </svg>
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="alert-bar alert-success" role="alert">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle
            cx="8"
            cy="8"
            r="7"
            stroke="currentColor"
            stroke-width="1.5"
          />
          <polyline
            points="5,8.5 7,10.5 11,6"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        {{ successMessage }}
      </div>

      <!-- Step Indicator -->
      <div class="step-indicator">
        <div
          v-for="(step, idx) in steps"
          :key="idx"
          class="step-item"
          :class="{ active: activeStep === idx, completed: activeStep > idx }"
          @click="goToStep(idx)"
        >
          <div class="step-circle">
            <svg
              v-if="activeStep > idx"
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
            >
              <polyline
                points="2,6.5 4.5,9 10,3.5"
                stroke="white"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span v-else>{{ idx + 1 }}</span>
          </div>
          <span class="step-label">{{ step.label }}</span>
        </div>
      </div>

      <!-- PIN Display -->
      <div class="pin-fields-row">
        <!-- <label class="pin-label">{{ steps[activeStep].label }}</label> -->
        <div class="pin-boxes">
          <div
            v-for="i in 4"
            :key="i"
            class="pin-box"
            :class="{
              filled: getCurrentPin.length >= i,
              active: getCurrentPin.length === i - 1,
            }"
          >
            <span v-if="getCurrentPin.length >= i" class="pin-dot">●</span>
          </div>
        </div>
      </div>

      <!-- Numpad -->
      <div class="numpad">
        <div class="numpad-row">
          <button class="numpad-key" @click="pressKey('1')">1</button>
          <button class="numpad-key" @click="pressKey('2')">2</button>
          <button class="numpad-key" @click="pressKey('3')">3</button>
        </div>
        <div class="numpad-row">
          <button class="numpad-key" @click="pressKey('4')">4</button>
          <button class="numpad-key" @click="pressKey('5')">5</button>
          <button class="numpad-key" @click="pressKey('6')">6</button>
        </div>
        <div class="numpad-row">
          <button class="numpad-key" @click="pressKey('7')">7</button>
          <button class="numpad-key" @click="pressKey('8')">8</button>
          <button class="numpad-key" @click="pressKey('9')">9</button>
        </div>
        <div class="numpad-row">
          <button class="numpad-key numpad-key--action" @click="clearAll">
            C
          </button>
          <button class="numpad-key" @click="pressKey('0')">0</button>
          <button class="numpad-key numpad-key--action" @click="backspace">
            <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
              <path
                d="M6 1L1 7L6 13H17V1H6Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linejoin="round"
              />
              <line
                x1="8.5"
                y1="4.5"
                x2="13.5"
                y2="9.5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
              <line
                x1="13.5"
                y1="4.5"
                x2="8.5"
                y2="9.5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="action-row">
        <button v-if="activeStep > 0" class="btn-back" @click="goBack">
          Back
        </button>
        <button
          class="btn-next"
          :class="{ 'full-width': activeStep === 0 }"
          :disabled="getCurrentPin.length < 4 || isLoading"
          @click="handleNext"
        >
          <span v-if="isLoading" class="spinner"></span>
          <span v-else>{{ activeStep === 2 ? "Reset PIN" : "Continue" }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import UserService from "../services/UserService";

export default {
  name: "ResetPinPassword",

  props: {
    /**
     * PIN already captured on blur from the login screen.
     * When provided, step 0 is pre-filled and skipped automatically.
     */
    prefillUserPin: {
      type: String,
      default: "",
    },
    currentUserId: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      steps: [
        { label: "Current PIN", key: "currentPin" },
        { label: "New PIN", key: "newPin" },
        { label: "Confirm PIN", key: "confirmPin" },
      ],
      activeStep: 0,
      currentPin: "",
      newPin: "",
      confirmPin: "",
      errorMessage: "",
      successMessage: "",
      isLoading: false,
    };
  },

  computed: {
    getCurrentPin() {
      return this[this.steps[this.activeStep].key];
    },
  },

  methods: {
    pressKey(digit) {
      this.errorMessage = "";
      const field = this.steps[this.activeStep].key;
      if (this[field].length < 4) {
        this[field] += digit;
        if (this[field].length === 4 && this.activeStep < 2) {
          setTimeout(() => this.handleNext(), 300);
        }
      }
    },

    backspace() {
      const field = this.steps[this.activeStep].key;
      this[field] = this[field].slice(0, -1);
    },

    clearAll() {
      const field = this.steps[this.activeStep].key;
      this[field] = "";
    },

    goToStep(idx) {
      if (idx < this.activeStep) {
        this.activeStep = idx;
        this.errorMessage = "";
      }
    },

    goBack() {
      if (this.activeStep > 0) {
        this.activeStep--;
        this.errorMessage = "";
      }
    },

    async handleNext() {
      this.errorMessage = "";
      if (this.getCurrentPin.length < 4)
        return this.$toasted.show("Plesae enter current pin");

      if (this.activeStep === 0) {
        this.activeStep = 1;
        return;
      }

      if (this.activeStep === 1) {
        if (this.newPin === this.currentPin) {
          this.errorMessage =
            "New PIN must be different from your current PIN.";
          this.newPin = "";
          return;
        }
        this.activeStep = 2;
        return;
      }

      if (this.activeStep === 2) {
        if (this.newPin !== this.confirmPin) {
          this.errorMessage =
            "PINs do not match. Please re-enter your new PIN.";
          this.confirmPin = "";
          return;
        }
        this.isLoading = true;
        try {
          const response = await this.submitReset();
          if (!response.status) {
            this.errorMessage = response["message"] || "Something went wrong";
            return;
          }
          this.successMessage = "PIN reset successfully! Redirecting…";
          setTimeout(() => this.$emit("reset-success"), 1500);
        } catch (error) {
          this.errorMessage = "Reset failed. Please try again.";
        } finally {
          this.isLoading = false;
        }
      }
    },

    async submitReset() {
      // return this.$http.post('/api/auth/reset-pin', { currentPin: this.currentPin, newPin: this.newPin })
      // return new Promise((resolve) => setTimeout(resolve, 900));
      try {
        const response = await UserService.updatePosUserPasswordPin({
          old_password_pin: this.currentPin,
          new_password_pin: this.newPin,
          user_id: this.currentUserId,
          user_pin: this.prefillUserPin,
        });

        const { data } = response;
        return data;
      } catch (error) {
        console.log(error);
        this.$toasted.show("Reset failed. Please try again.");
      }
    },
  },
};
</script>

<style scoped>
/* ── Panel shell — mirrors the login right-panel sizing ──────── */
.reset-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.reset-card {
  width: 100%;
  max-width: 460px;
}

/* ── Header ──────────────────────────────────────────────────── */
.terminal-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.5px;
  color: #999;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.greeting {
  font-size: 30px;
  font-weight: 800;
  color: #1a1a2e;
  margin: 0 0 6px;
  letter-spacing: -0.5px;
}

.subtitle {
  font-size: 13.5px;
  color: #888;
  margin-bottom: 20px;
  line-height: 1.5;
}

/* ── Alerts ──────────────────────────────────────────────────── */
.alert-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 16px;
}

.alert-error {
  background: #fff0f0;
  color: #c0392b;
  border: 1px solid #fcd5d5;
}

.alert-success {
  background: #f0faf4;
  color: #27ae60;
  border: 1px solid #c3e6cb;
}

/* ── Step Indicator ──────────────────────────────────────────── */
.step-indicator {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  flex: 1;
  position: relative;
}

.step-item:not(:last-child)::after {
  content: "";
  flex: 1;
  height: 2px;
  background: #e8e8f0;
  margin-right: 12px; /* Adds space before the next circle */
  transition: background 0.25s;
}

.step-item.completed:not(:last-child)::after {
  background: #7b5cc8;
}

.step-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #e0ddf0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #aaa;
  background: #fff;
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.step-item.active .step-circle {
  border-color: #7b5cc8;
  color: #7b5cc8;
  background: #f3effe;
}

.step-item.completed .step-circle {
  border-color: #7b5cc8;
  background: #7b5cc8;
  color: #fff;
}

.step-label {
  font-size: 11.5px;
  font-weight: 600;
  color: #bbb;
  white-space: nowrap;
  transition: color 0.2s;
}

.step-item.active .step-label,
.step-item.completed .step-label {
  color: #7b5cc8;
}

/* ── PIN Display ─────────────────────────────────────────────── */
.pin-fields-row {
  margin-bottom: 20px;
}

.pin-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #555;
  margin-bottom: 10px;
}

.pin-boxes {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.pin-box {
  width: 40px;
  height: 40px;
  border: 2px solid #e0ddf0;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}

.pin-box.active {
  border-color: #7b5cc8;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(123, 92, 200, 0.12);
}

.pin-box.filled {
  border-color: #7b5cc8;
  background: #f3effe;
}

.pin-dot {
  color: #7b5cc8;
  font-size: 14px;
  line-height: 1;
}

/* ── Numpad ──────────────────────────────────────────────────── */
.numpad {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}

.numpad-row {
  display: flex;
  gap: 10px;
}

.numpad-key {
  flex: 1;
  height: 38px;
  width: 30px;
  border: 1.5px solid #ebebf5;
  border-radius: 12px;
  background: #fff;
  font-size: 18px;
  font-weight: 600;
  color: #2d2d4e;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s, border-color 0.12s, transform 0.08s,
    box-shadow 0.12s;
  user-select: none;
}

.numpad-key:hover {
  background: #f3effe;
  border-color: #c3b5ee;
}

.numpad-key:active {
  transform: scale(0.94);
  background: #ece4fc;
  box-shadow: 0 0 0 2px rgba(123, 92, 200, 0.15);
}

.numpad-key--action {
  color: #888;
  font-size: 14px;
  font-weight: 700;
}

.numpad-key--action:hover {
  background: #fff5f5;
  border-color: #f0c0c0;
  color: #c0392b;
}

/* ── Action Buttons ──────────────────────────────────────────── */
.action-row {
  display: flex;
  gap: 12px;
}

.btn-back {
  flex: 0 0 90px;
  height: 50px;
  border: 2px solid #e0ddf0;
  border-radius: 12px;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: #888;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-back:hover {
  border-color: #b0a8d8;
  color: #6b4fbb;
  background: #f7f5ff;
}

.btn-next {
  flex: 1;
  height: 50px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #8b6bd8 0%, #6b4fbb 100%);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 16px rgba(107, 79, 187, 0.35);
  letter-spacing: 0.2px;
  transition: opacity 0.15s, transform 0.1s, box-shadow 0.15s;
}

.btn-next.full-width {
  flex: 1 1 100%;
}

.btn-next:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  box-shadow: none;
}

.btn-next:not(:disabled):hover {
  box-shadow: 0 6px 20px rgba(107, 79, 187, 0.45);
  transform: translateY(-1px);
}

.btn-next:not(:disabled):active {
  transform: translateY(0);
}

/* ── Spinner ─────────────────────────────────────────────────── */
.spinner {
  width: 18px;
  height: 18px;
  border: 2.5px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  display: inline-block;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/PosResetPasswordPin.vue